import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { EventNote, RoomOutlined } from '@material-ui/icons';
import SurfaceCard from '../SurfaceCard';
import StatusPill from '../StatusPill';
import { lightTheme as theme } from '../../theme';
import {
    normalizeMaintenanceType,
    getMaintenanceExecutionLabel,
} from '../../utils/maintenanceTypeHelpers';

const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
`;

const QuoteId = styled.span`
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
`;

const QuoteDate = styled.span`
    color: #94a3b8;
    font-size: 12px;
    font-weight: 500;
`;

const QuoteValue = styled.p`
    margin: 0;
    color: #0f172a;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
`;

const Badges = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

const Address = styled.p`
    margin: 12px 0 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.4;
`;

function formatDate(value) {
    if (!value) return '--/--/----';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--/--/----';
    return date.toLocaleDateString('pt-BR');
}

function formatValue(value, status, waitingLabel) {
    if (value === null || value === undefined || Number(value) === 0) {
        if ((status || '').toLowerCase().includes('cancelado') || (status || '').toLowerCase().includes('recusado')) {
            return 'R$ 0,00';
        }
        return waitingLabel || 'Orcamento em analise';
    }

    return Number(value).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
}

function getMaintenanceData(quote) {
    const normalizedType =
        quote.maintenanceType ||
        normalizeMaintenanceType({
            tipoManutencao: quote.tipoManutencao,
            maintenanceLocalization: quote.maintenanceLocalization,
            endereco: quote.endereco,
        });

    const maintenanceLabel =
        quote.maintenanceExecutionLabel ||
        getMaintenanceExecutionLabel({
            tipoManutencao: quote.tipoManutencao,
            maintenanceLocalization: quote.maintenanceLocalization,
            endereco: quote.endereco,
        });

    const isWorkshop = normalizedType === 'oficina';
    return {
        label: maintenanceLabel,
        indicatorColor: isWorkshop ? theme.colors.success : theme.colors.info,
        variant: isWorkshop ? 'maintenanceWorkshop' : 'maintenanceInCompany',
    };
}

function QuotationCard({
    quote,
    onClick,
    waitingLabel,
    statusVariant,
}) {
    const maintenance = getMaintenanceData(quote);

    return (
        <SurfaceCard
            onClick={onClick}
            leftIndicatorColor={maintenance.indicatorColor}
            padding='12px 12px 12px 16px'
            radius={8}
            borderColor='transparent'
            shadow='0 2px 4px rgba(0, 0, 0, 0.05)'
            style={{ width: '100%', textAlign: 'left' }}
        >
            <TopRow>
                <QuoteId>#{quote.refId || quote.id}</QuoteId>
                <QuoteDate>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <EventNote style={{ fontSize: 14, opacity: 0.72 }} />
                        {formatDate(quote.createdAt)}
                    </span>
                </QuoteDate>
            </TopRow>

            <QuoteValue>{formatValue(quote.valor, quote.status, waitingLabel)}</QuoteValue>

            <Badges>
                <StatusPill variant={statusVariant}>{quote.status || '-'}</StatusPill>
                <StatusPill variant={maintenance.variant}>{maintenance.label}</StatusPill>
            </Badges>

            {quote.endereco ? (
                <Address>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <RoomOutlined style={{ fontSize: 14, opacity: 0.72 }} />
                        {quote.endereco}
                    </span>
                </Address>
            ) : null}
        </SurfaceCard>
    );
}

QuotationCard.propTypes = {
    quote: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        refId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        valor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        endereco: PropTypes.string,
        maintenanceType: PropTypes.string,
        maintenanceExecutionLabel: PropTypes.string,
        tipoManutencao: PropTypes.string,
        maintenanceLocalization: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func.isRequired,
    waitingLabel: PropTypes.string,
    statusVariant: PropTypes.oneOf(['danger', 'info', 'success', 'warning', 'neutral']),
};

QuotationCard.defaultProps = {
    waitingLabel: undefined,
    statusVariant: 'neutral',
};

export default QuotationCard;

